/* ============================================================================
   SBD colourways — swap between colourways on the product page without a
   visible page load.

   Every colourway is a separate Shopify product, so each swatch is a real link
   to that product's own URL. This script upgrades those links: instead of
   navigating, it fetches the target product's main section, swaps it in, and
   pushes the new URL. The address bar, the back button and a page refresh all
   behave exactly as they would have done.

   ---------------------------------------------------------------------------
   THE SAFETY RULE
   ---------------------------------------------------------------------------
   Nothing here may leave a customer stuck on a page showing one colourway's
   photos next to another's price. So every failure path ends in a real
   navigation to the href that was clicked:

     - fetch rejects or returns a non-200      -> navigate
     - the response has no matching section    -> navigate
     - anything at all throws                  -> navigate

   The click is only cancelled once the replacement markup is in hand.

   ---------------------------------------------------------------------------
   RE-BINDING AFTER A SWAP
   ---------------------------------------------------------------------------
   Replacing the section's HTML leaves the new gallery and size picker without
   their event handlers. Impulse, like every Archetype theme, binds product
   sections in response to `shopify:section:load` — that is how the theme
   editor re-initialises a section after an edit — so the same event is
   dispatched here after the swap.

   That is the one part of this that cannot be verified without rendering the
   theme. If the gallery or the size buttons come up dead after switching
   colour, set SBD_CW_INSTANT to false in the snippet that loads this file and
   the swatches go back to being plain links, which always work.
   ========================================================================== */
(function () {
  'use strict';

  var SECTION_ID = 'main';
  var SECTION_EL_ID = 'shopify-section-' + SECTION_ID;

  if (window.SBD_CW_INSTANT === false) return;

  function sectionEl() {
    return document.getElementById(SECTION_EL_ID);
  }

  /* A plain navigation. Used for every failure path, and deliberately not
     wrapped in any error handling of its own. */
  function fallback(url) {
    window.location.href = url;
  }

  function setBusy(on) {
    var rows = document.querySelectorAll('[data-sbd-cw]');
    for (var i = 0; i < rows.length; i++) {
      rows[i].classList.toggle('is-busy', !!on);
    }
  }

  /* Pull the replacement section out of a fetched product page.
     `?section_id=` returns that section on its own, already unwrapped, which
     avoids parsing a whole document. */
  function fetchSection(url) {
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    return fetch(url + sep + 'section_id=' + SECTION_ID, {
      credentials: 'same-origin'
    }).then(function (res) {
      if (!res.ok) throw new Error('section fetch failed: ' + res.status);
      return res.text();
    });
  }

  function apply(url, html) {
    var host = sectionEl();
    if (!host) throw new Error('no product section on the page');

    var holder = document.createElement('div');
    holder.innerHTML = html;

    /* section_id responses are sometimes wrapped in the section div and
       sometimes not, depending on the section type. Handle both. */
    var incoming = holder.querySelector('#' + SECTION_EL_ID) || holder;
    var markup = incoming === holder ? holder.innerHTML : incoming.innerHTML;
    if (!markup || !markup.trim()) throw new Error('empty section markup');

    host.innerHTML = markup;

    history.pushState({ sbdCw: true }, '', url);

    /* Ask the theme to bind the section again, the same way the theme editor
       does after an edit. */
    document.dispatchEvent(
      new CustomEvent('shopify:section:load', {
        bubbles: true,
        detail: { sectionId: SECTION_ID }
      })
    );
    host.dispatchEvent(
      new CustomEvent('shopify:section:load', {
        bubbles: true,
        detail: { sectionId: SECTION_ID }
      })
    );

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function onClick(e) {
    var dot = e.target.closest ? e.target.closest('.sbd-cw--product .sbd-cw__dot') : null;
    if (!dot) return;

    /* Let the browser handle anything that is not a plain left click, so
       open-in-new-tab and friends keep working. */
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

    var url = dot.getAttribute('data-sbd-cw-url') || dot.getAttribute('href');
    if (!url) return;
    if (dot.classList.contains('is-current')) {
      e.preventDefault();
      return;
    }

    e.preventDefault();
    setBusy(true);

    fetchSection(url)
      .then(function (html) {
        apply(url, html);
        setBusy(false);
      })
      .catch(function () {
        /* Deliberately silent: the customer gets the page they asked for, by
           the ordinary route. */
        setBusy(false);
        fallback(url);
      });
  }

  /* Back and forward across swapped colourways. Re-fetch rather than cache the
     markup, so price and stock are never stale. */
  function onPop(e) {
    if (!e.state || !e.state.sbdCw) return;
    var url = window.location.pathname + window.location.search;
    fetchSection(url)
      .then(function (html) {
        var host = sectionEl();
        if (!host) throw new Error('no product section');
        var holder = document.createElement('div');
        holder.innerHTML = html;
        var incoming = holder.querySelector('#' + SECTION_EL_ID) || holder;
        host.innerHTML = incoming === holder ? holder.innerHTML : incoming.innerHTML;
        document.dispatchEvent(
          new CustomEvent('shopify:section:load', {
            bubbles: true,
            detail: { sectionId: SECTION_ID }
          })
        );
      })
      .catch(function () {
        window.location.reload();
      });
  }

  /* Delegated from the document so the handler survives the swap replacing
     the row it was bound to. */
  document.addEventListener('click', onClick);
  window.addEventListener('popstate', onPop);

  /* Warm the cache on intent. Even when the swap is disabled or fails, the
     ordinary navigation that follows is then close to instant. */
  var prefetched = {};
  document.addEventListener(
    'mouseover',
    function (e) {
      var dot = e.target.closest ? e.target.closest('.sbd-cw__dot') : null;
      if (!dot) return;
      var url = dot.getAttribute('data-sbd-cw-url');
      if (!url || prefetched[url]) return;
      prefetched[url] = true;
      var link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url;
      document.head.appendChild(link);
    },
    { passive: true }
  );
})();
